/**
 * Kenzu Real Experiences
 * Carrusel continuo, infinito, arrastrable y compatible con Shopify 2.0.
 */

(() => {
  "use strict";

  const SECTION_SELECTOR = "[data-kenzu-experiences]";
  const instances = new WeakMap();

  class KenzuExperiences {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector("[data-kenzu-viewport]");
      this.track = section.querySelector("[data-kenzu-track]");
      this.originalGroup = section.querySelector("[data-kenzu-group]");

      if (!this.viewport || !this.track || !this.originalGroup) return;

      this.autoplay =
        section.dataset.autoplay === "true" &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      this.speed = Math.max(
        0,
        Number.parseFloat(section.dataset.speed || "35")
      );

      this.direction = section.dataset.direction === "right" ? 1 : -1;
      this.pauseOnHover = section.dataset.pauseHover === "true";
      this.draggable = section.dataset.draggable === "true";

      this.prevButton = section.querySelector("[data-kenzu-prev]");
      this.nextButton = section.querySelector("[data-kenzu-next]");
      this.toggleButton = section.querySelector("[data-kenzu-toggle]");

      this.position = 0;
      this.groupWidth = 0;
      this.lastTimestamp = 0;
      this.animationFrame = null;
      this.isPaused = !this.autoplay;
      this.isHovering = false;
      this.isDragging = false;
      this.hasDragged = false;
      this.pointerId = null;
      this.dragStartX = 0;
      this.dragStartPosition = 0;
      this.lastPointerX = 0;
      this.lastPointerTime = 0;
      this.velocity = 0;
      this.resizeTimer = null;

      this.boundAnimate = this.animate.bind(this);
      this.boundResize = this.handleResize.bind(this);

      this.init();
    }

    init() {
      this.createClones();
      this.measure();
      this.bindEvents();
      this.updateToggleButton();
      this.render();

      if (this.autoplay) {
        this.start();
      }
    }

    createClones() {
      this.track
        .querySelectorAll("[data-kenzu-clone]")
        .forEach((clone) => clone.remove());

      const clone = this.originalGroup.cloneNode(true);
      clone.dataset.kenzuClone = "true";
      clone.removeAttribute("data-kenzu-group");
      clone.setAttribute("aria-hidden", "true");

      clone.querySelectorAll("[id]").forEach((element) => {
        element.removeAttribute("id");
      });

      clone.querySelectorAll("a, button, input, select, textarea").forEach(
        (element) => {
          element.setAttribute("tabindex", "-1");
        }
      );

      clone
        .querySelectorAll("[data-shopify-editor-block]")
        .forEach((element) => {
          element.removeAttribute("data-shopify-editor-block");
        });

      this.track.appendChild(clone);
    }

    measure() {
      this.groupWidth = this.originalGroup.getBoundingClientRect().width;

      if (!this.groupWidth) return;

      while (this.position <= -this.groupWidth) {
        this.position += this.groupWidth;
      }

      while (this.position > 0) {
        this.position -= this.groupWidth;
      }

      this.render();
    }

    bindEvents() {
      window.addEventListener("resize", this.boundResize, {
        passive: true
      });

      document.addEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );

      if (this.pauseOnHover) {
        this.viewport.addEventListener("mouseenter", this.handleMouseEnter);
        this.viewport.addEventListener("mouseleave", this.handleMouseLeave);
      }

      if (this.draggable) {
        this.viewport.addEventListener(
          "pointerdown",
          this.handlePointerDown
        );

        this.viewport.addEventListener(
          "pointermove",
          this.handlePointerMove
        );

        this.viewport.addEventListener(
          "pointerup",
          this.handlePointerUp
        );

        this.viewport.addEventListener(
          "pointercancel",
          this.handlePointerUp
        );

        this.viewport.addEventListener(
          "dragstart",
          this.preventNativeDrag
        );

        this.viewport.addEventListener(
          "click",
          this.preventClickAfterDrag,
          true
        );
      }

      this.prevButton?.addEventListener("click", this.handlePrevious);
      this.nextButton?.addEventListener("click", this.handleNext);
      this.toggleButton?.addEventListener("click", this.handleToggle);

      this.viewport.addEventListener("keydown", this.handleKeydown);
    }

    handleMouseEnter = () => {
      this.isHovering = true;
    };

    handleMouseLeave = () => {
      this.isHovering = false;
    };

    handleVisibilityChange = () => {
      this.lastTimestamp = 0;
    };

    preventNativeDrag = (event) => {
      event.preventDefault();
    };

    handlePointerDown = (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;

      this.isDragging = true;
      this.hasDragged = false;
      this.pointerId = event.pointerId;
      this.dragStartX = event.clientX;
      this.dragStartPosition = this.position;
      this.lastPointerX = event.clientX;
      this.lastPointerTime = performance.now();
      this.velocity = 0;

      this.viewport.classList.add("is-dragging");
      this.viewport.setPointerCapture?.(event.pointerId);
    };

    handlePointerMove = (event) => {
      if (!this.isDragging || event.pointerId !== this.pointerId) return;

      const deltaX = event.clientX - this.dragStartX;
      const now = performance.now();
      const elapsed = Math.max(1, now - this.lastPointerTime);

      this.position = this.dragStartPosition + deltaX;
      this.velocity =
        (event.clientX - this.lastPointerX) / elapsed;

      this.lastPointerX = event.clientX;
      this.lastPointerTime = now;

      if (Math.abs(deltaX) > 6) {
        this.hasDragged = true;
      }

      this.normalizePosition();
      this.render();
    };

    handlePointerUp = (event) => {
      if (!this.isDragging || event.pointerId !== this.pointerId) return;

      this.isDragging = false;
      this.pointerId = null;
      this.viewport.classList.remove("is-dragging");

      this.viewport.releasePointerCapture?.(event.pointerId);

      if (Math.abs(this.velocity) > 0.05) {
        this.position += this.velocity * 150;
        this.normalizePosition();
        this.render();
      }

      window.setTimeout(() => {
        this.hasDragged = false;
      }, 80);
    };

    preventClickAfterDrag = (event) => {
      if (!this.hasDragged) return;

      event.preventDefault();
      event.stopPropagation();
    };

    handlePrevious = () => {
      const amount = this.getStep();

      this.position += amount;
      this.normalizePosition();
      this.render();
      this.restartTimestamp();
    };

    handleNext = () => {
      const amount = this.getStep();

      this.position -= amount;
      this.normalizePosition();
      this.render();
      this.restartTimestamp();
    };

    handleToggle = () => {
      this.isPaused = !this.isPaused;
      this.updateToggleButton();
      this.restartTimestamp();

      if (!this.animationFrame && !this.isPaused) {
        this.start();
      }
    };

    handleKeydown = (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.handlePrevious();
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        this.handleNext();
      }

      if (event.key === " ") {
        event.preventDefault();
        this.handleToggle();
      }
    };

    handleResize() {
      window.clearTimeout(this.resizeTimer);

      this.resizeTimer = window.setTimeout(() => {
        this.measure();
      }, 120);
    }

    getStep() {
      const card = this.originalGroup.querySelector("[data-kenzu-card]");

      if (!card) {
        return Math.min(this.viewport.clientWidth * 0.8, 360);
      }

      const groupStyles = window.getComputedStyle(this.originalGroup);
      const gap = Number.parseFloat(groupStyles.columnGap || "0");

      return card.getBoundingClientRect().width + gap;
    }

    updateToggleButton() {
      if (!this.toggleButton) return;

      this.toggleButton.setAttribute(
        "aria-pressed",
        String(this.isPaused)
      );

      this.toggleButton.setAttribute(
        "aria-label",
        this.isPaused
          ? "Reanudar movimiento"
          : "Pausar movimiento"
      );
    }

    start() {
      this.stop();
      this.animationFrame = requestAnimationFrame(this.boundAnimate);
    }

    stop() {
      if (!this.animationFrame) return;

      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    restartTimestamp() {
      this.lastTimestamp = 0;
    }

    animate(timestamp) {
      if (!this.lastTimestamp) {
        this.lastTimestamp = timestamp;
      }

      const deltaTime = Math.min(
        50,
        timestamp - this.lastTimestamp
      );

      this.lastTimestamp = timestamp;

      const shouldMove =
        this.autoplay &&
        !this.isPaused &&
        !this.isDragging &&
        !this.isHovering &&
        !document.hidden &&
        this.groupWidth > 0;

      if (shouldMove) {
        this.position +=
          this.direction * this.speed * (deltaTime / 1000);

        this.normalizePosition();
        this.render();
      }

      this.animationFrame = requestAnimationFrame(this.boundAnimate);
    }

    normalizePosition() {
      if (!this.groupWidth) return;

      while (this.position <= -this.groupWidth) {
        this.position += this.groupWidth;
      }

      while (this.position > 0) {
        this.position -= this.groupWidth;
      }
    }

    render() {
      this.track.style.transform =
        `translate3d(${this.position}px, 0, 0)`;
    }

    destroy() {
      this.stop();
      window.clearTimeout(this.resizeTimer);

      window.removeEventListener("resize", this.boundResize);
      document.removeEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );

      this.viewport.removeEventListener(
        "mouseenter",
        this.handleMouseEnter
      );

      this.viewport.removeEventListener(
        "mouseleave",
        this.handleMouseLeave
      );

      this.viewport.removeEventListener(
        "pointerdown",
        this.handlePointerDown
      );

      this.viewport.removeEventListener(
        "pointermove",
        this.handlePointerMove
      );

      this.viewport.removeEventListener(
        "pointerup",
        this.handlePointerUp
      );

      this.viewport.removeEventListener(
        "pointercancel",
        this.handlePointerUp
      );

      this.viewport.removeEventListener(
        "dragstart",
        this.preventNativeDrag
      );

      this.viewport.removeEventListener(
        "click",
        this.preventClickAfterDrag,
        true
      );

      this.viewport.removeEventListener(
        "keydown",
        this.handleKeydown
      );

      this.prevButton?.removeEventListener(
        "click",
        this.handlePrevious
      );

      this.nextButton?.removeEventListener(
        "click",
        this.handleNext
      );

      this.toggleButton?.removeEventListener(
        "click",
        this.handleToggle
      );

      this.track
        .querySelectorAll("[data-kenzu-clone]")
        .forEach((clone) => clone.remove());
    }
  }

  const initialize = (root = document) => {
    const sections = [];

    if (root.matches?.(SECTION_SELECTOR)) {
      sections.push(root);
    }

    root.querySelectorAll?.(SECTION_SELECTOR).forEach((section) => {
      sections.push(section);
    });

    sections.forEach((section) => {
      if (instances.has(section)) return;

      instances.set(section, new KenzuExperiences(section));
    });
  };

  const destroy = (root) => {
    const sections = [];

    if (root.matches?.(SECTION_SELECTOR)) {
      sections.push(root);
    }

    root.querySelectorAll?.(SECTION_SELECTOR).forEach((section) => {
      sections.push(section);
    });

    sections.forEach((section) => {
      instances.get(section)?.destroy();
      instances.delete(section);
    });
  };

  const boot = () => initialize(document);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, {
      once: true
    });
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initialize(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroy(event.target);
  });

  document.addEventListener("shopify:block:select", (event) => {
    const section = event.target.closest?.(SECTION_SELECTOR);

    if (!section) return;

    const instance = instances.get(section);

    if (instance) {
      instance.isPaused = true;
      instance.updateToggleButton();
    }
  });
})();